import { useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import type { Deal } from '@bcc/shared';
import { DEAL_STATUSES } from '@bcc/shared';
import { envConfig } from '../env';
import { apiFetch } from '../lib/api';
import { resolveDealCoverUrl } from '../lib/dealCover';
import { useAuth } from '../contexts/AuthContext';

/** True when embedded API origin is the same hostname as the SPA — often a mistaken *-client URL. */
function apiBasePointsAtCurrentHost(baseRaw: string): boolean {
  const base = baseRaw.trim().replace(/\/+$/, '');
  if (!base || typeof window === 'undefined') return false;
  try {
    const h = new URL(base).hostname;
    return Boolean(h) && h === window.location.hostname;
  } catch {
    return false;
  }
}

const STATE_FILTERS = [
  { label: 'Alabama', test: /\b(AL|ALABAMA)\b/i },
  { label: 'Arizona', test: /\b(AZ|ARIZONA)\b/i },
  { label: 'Florida', test: /\b(FL|FLORIDA)\b/i },
  { label: 'Georgia', test: /\b(GA|GEORGIA)\b/i },
  { label: 'Virginia', test: /\b(VA|VIRGINIA)\b/i },
  { label: 'Maryland', test: /\b(MD|MARYLAND)\b/i },
  { label: 'Texas', test: /\b(TX|TEXAS)\b/i },
] as const;

function dealTextBlob(d: Deal) {
  return `${d.title}\n${d.description}`;
}

function regionForDeal(d: Deal): string | null {
  if (d.state) {
    const st = d.state.trim();
    for (const { label, test } of STATE_FILTERS) {
      if (test.test(st)) return label;
    }
    return st;
  }
  const t = dealTextBlob(d);
  for (const { label, test } of STATE_FILTERS) {
    if (test.test(t)) return label;
  }
  return null;
}

/** City, ST style — prefers structured fields from admin upload */
function locationLine(d: Deal): string {
  if (d.city && d.state) return `${d.city}, ${d.state}`;
  const t = `${d.title} · ${d.description}`;
  const m = t.match(/([A-Z][a-z]+(?:\s[A-Z][a-z]+)*),\s*(AL|AK|AZ|AR|CA|CO|FL|GA|MD|TX|NY|NJ|TN|NC|SC|VA)/);
  if (m) return `${m[1]}, ${m[2]}`;
  const r = regionForDeal(d);
  return r ? `${r}` : 'U.S. private placement';
}

function badgeLabel(d: Deal): string {
  const t = d.title.trim();
  if (t.length <= 42) return t;
  return `${t.slice(0, 40)}…`;
}

const ISO_NOW = new Date().toISOString();

/** Shown when the API returns no deals — illustrative listings across target states; Class A–D + strategic pills on cards. */
const EXAMPLE_DEALS: Deal[] = [
  {
    id: 'example-al',
    title: 'River District I',
    description: 'Garden courtyard multifamily · Birmingham, AL · illustrative sample.',
    irr: 15.2,
    equity_multiple: 1.85,
    min_investment: 50_000,
    status: DEAL_STATUSES.OPEN,
    image_paths: null,
    created_at: ISO_NOW,
    updated_at: ISO_NOW,
  },
  {
    id: 'example-az',
    title: 'Sonoran Ridge',
    description: 'Low-rise wrap Phoenix MSA · Scottsdale, AZ · illustrative sample.',
    irr: 13.8,
    equity_multiple: 1.72,
    min_investment: 75_000,
    status: DEAL_STATUSES.OPEN,
    image_paths: null,
    created_at: ISO_NOW,
    updated_at: ISO_NOW,
  },
  {
    id: 'example-fl',
    title: 'Coastal Commerce Lofts',
    description: 'Mixed-use podium · Tampa, FL · illustrative sample.',
    irr: 17.1,
    equity_multiple: 1.95,
    min_investment: 100_000,
    status: DEAL_STATUSES.OPEN,
    image_paths: null,
    created_at: ISO_NOW,
    updated_at: ISO_NOW,
  },
  {
    id: 'example-ga',
    title: 'Midtown Annex',
    description: 'Mid-rise value-add · Atlanta, GA · illustrative sample.',
    irr: 16.4,
    equity_multiple: 1.9,
    min_investment: 75_000,
    status: DEAL_STATUSES.OPEN,
    image_paths: null,
    created_at: ISO_NOW,
    updated_at: ISO_NOW,
  },
  {
    id: 'example-va',
    title: 'Potomac Quarter',
    description: 'Elevator product · Arlington, VA · illustrative sample.',
    irr: 14.5,
    equity_multiple: 1.78,
    min_investment: 100_000,
    status: DEAL_STATUSES.OPEN,
    image_paths: null,
    created_at: ISO_NOW,
    updated_at: ISO_NOW,
  },
  {
    id: 'example-md',
    title: 'Harbor Works',
    description: 'Harbor-adjacent rehab candidate · Baltimore, MD · illustrative sample.',
    irr: 18.0,
    equity_multiple: 2.05,
    min_investment: 50_000,
    status: DEAL_STATUSES.OPEN,
    image_paths: null,
    created_at: ISO_NOW,
    updated_at: ISO_NOW,
  },
  {
    id: 'example-tx',
    title: 'Austin Tech Corridor',
    description: 'Ground-up BTS residential · Austin, TX · illustrative sample.',
    irr: 19.2,
    equity_multiple: 2.1,
    min_investment: 150_000,
    status: DEAL_STATUSES.OPEN,
    image_paths: null,
    created_at: ISO_NOW,
    updated_at: ISO_NOW,
  },
  {
    id: 'example-08',
    title: 'Cottonwood Commons',
    description: 'Institutional garden wrap · Montgomery, AL · illustrative sample.',
    irr: 14.8,
    equity_multiple: 1.79,
    min_investment: 50_000,
    status: DEAL_STATUSES.OPEN,
    image_paths: null,
    created_at: ISO_NOW,
    updated_at: ISO_NOW,
  },
  {
    id: 'example-09',
    title: 'Camelback Midrise',
    description: 'Urban infill elevator · Phoenix, AZ · illustrative sample.',
    irr: 15.5,
    equity_multiple: 1.82,
    min_investment: 100_000,
    status: DEAL_STATUSES.OPEN,
    image_paths: null,
    created_at: ISO_NOW,
    updated_at: ISO_NOW,
  },
  {
    id: 'example-10',
    title: 'Lake Eola Reserve',
    description: 'High-rise rental tower · Orlando, FL · illustrative sample.',
    irr: 16.2,
    equity_multiple: 1.88,
    min_investment: 100_000,
    status: DEAL_STATUSES.OPEN,
    image_paths: null,
    created_at: ISO_NOW,
    updated_at: ISO_NOW,
  },
  {
    id: 'example-11',
    title: 'Energy Corridor Plaza',
    description: 'Podium parking wrap · Houston, TX · illustrative sample.',
    irr: 17.8,
    equity_multiple: 1.98,
    min_investment: 125_000,
    status: DEAL_STATUSES.OPEN,
    image_paths: null,
    created_at: ISO_NOW,
    updated_at: ISO_NOW,
  },
  {
    id: 'example-12',
    title: 'James River Landing',
    description: 'Waterfront mid-rise · Richmond, VA · illustrative sample.',
    irr: 15.0,
    equity_multiple: 1.8,
    min_investment: 75_000,
    status: DEAL_STATUSES.OPEN,
    image_paths: null,
    created_at: ISO_NOW,
    updated_at: ISO_NOW,
  },
];

/** Strategic tags only; condition uses Class A–D on example cards. */
const EXAMPLE_TAGS: Record<string, { condition: string; strategicTag: string }> = {
  'example-al': { condition: 'Class A', strategicTag: 'Core' },
  'example-az': { condition: 'Class A', strategicTag: 'Cash Flow' },
  'example-fl': { condition: 'Class B', strategicTag: 'Value-Add' },
  'example-ga': { condition: 'Class B', strategicTag: 'Appreciation' },
  'example-va': { condition: 'Class C', strategicTag: 'Core Plus' },
  'example-md': { condition: 'Class C', strategicTag: 'Tax Advantage' },
  'example-tx': { condition: 'Class D', strategicTag: 'Opportunistic' },
  'example-08': { condition: 'Class B', strategicTag: 'Income-Producing' },
  'example-09': { condition: 'Class A', strategicTag: 'Institutional Grade' },
  'example-10': { condition: 'Class B', strategicTag: 'Core Plus' },
  'example-11': { condition: 'Class C', strategicTag: 'Repositioning' },
  'example-12': { condition: 'Class D', strategicTag: 'Redevelopment' },
};

const EXAMPLE_TAGS_BY_TITLE: Record<string, { condition: string; strategicTag: string }> = {};
for (const d of EXAMPLE_DEALS) {
  const t = EXAMPLE_TAGS[d.id];
  if (t) EXAMPLE_TAGS_BY_TITLE[d.title] = t;
}

/** Large-scale multifamily exteriors — mid/high-rise & multi-building complexes (Unsplash). */
const EXAMPLE_COVER_URLS: Record<string, string> = {
  'example-al':
    'https://images.unsplash.com/photo-1769147555077-2a4d50639695?auto=format&fit=crop&w=1600&q=82',
  'example-az':
    'https://images.unsplash.com/photo-1758957803927-9c865d07fbe2?auto=format&fit=crop&w=1600&q=82',
  'example-fl':
    'https://images.unsplash.com/photo-1758938388907-e88304c4f535?auto=format&fit=crop&w=1600&q=82',
  'example-ga':
    'https://images.unsplash.com/photo-1772409345810-c9f86e17b816?auto=format&fit=crop&w=1600&q=82',
  'example-va':
    'https://images.unsplash.com/photo-1675118546055-32c56579cc58?auto=format&fit=crop&w=1600&q=82',
  'example-md':
    'https://images.unsplash.com/photo-1681352238705-add3586d86e0?auto=format&fit=crop&w=1600&q=82',
  'example-tx':
    'https://images.unsplash.com/photo-1759277700809-2b544c0f4261?auto=format&fit=crop&w=1600&q=82',
  'example-08':
    'https://images.unsplash.com/photo-1723817709007-5633af33c668?auto=format&fit=crop&w=1600&q=82',
  'example-09':
    'https://images.unsplash.com/photo-1755019859975-bacffb138665?auto=format&fit=crop&w=1600&q=82',
  /* Local asset — previous Unsplash id (1545324418) often failed to load in production */
  'example-10': '/deals-samples/lake-eola-mf.jpg',
  'example-11':
    'https://images.unsplash.com/photo-1681352238705-add3586d86e0?auto=format&fit=crop&w=1600&q=82',
  'example-12':
    'https://images.unsplash.com/photo-1769147555077-2a4d50639695?auto=format&fit=crop&w=1600&q=82',
};

function resolveCover(d: Deal): string | null {
  return resolveDealCoverUrl(d, EXAMPLE_COVER_URLS);
}

const CARD_SHELL =
  'group relative block w-full aspect-[16/13] min-h-[14rem] overflow-hidden rounded-2xl border border-white/10 shadow-[0_24px_60px_-30px_rgb(0_0_0/0.8)] outline-none ring-0 transition hover:-translate-y-0.5 hover:border-white/20 focus-visible:ring-2 focus-visible:ring-[var(--header-shell-bg)]';

function DealCard({
  d,
  condition,
  strategicTag,
}: {
  d: Deal;
  condition?: string;
  strategicTag?: string;
}) {
  const cover = resolveCover(d);
  const isExample = d.id.startsWith('example-');
  const showTagColumn = Boolean(condition || strategicTag);

  const cardInner = (
    <>
      <div
        className="absolute inset-0 bg-gradient-to-br from-[#1a2520] via-[#243028] to-[var(--home-zone-void)]"
        style={
          cover
            ? {
                backgroundImage: `linear-gradient(to top, rgb(10 18 36 / 0.88) 0%, rgb(10 18 36 / 0.2) 45%, transparent 72%), url(${cover})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      />
      <div className={`absolute left-3 top-3 max-w-[min(72%,240px)] ${showTagColumn ? 'sm:max-w-[min(58%,200px)]' : ''}`}>
        <span className="inline-block rounded-md bg-neutral-950/75 px-2.5 py-1 font-display text-[0.58rem] font-semibold uppercase leading-tight tracking-[0.04em] text-white ring-1 ring-white/10 backdrop-blur-sm">
          {badgeLabel(d)}
        </span>
      </div>

      {showTagColumn ? (
        <div className="absolute right-3 top-3 flex max-w-[min(52%,220px)] flex-col items-end gap-1.5 text-right">
          {condition ? (
            <span className="rounded-md bg-black/55 px-2.5 py-1 font-display text-[0.56rem] font-semibold uppercase leading-tight tracking-[0.05em] text-white ring-1 ring-white/15 backdrop-blur-sm">
              {condition}
            </span>
          ) : null}
          {strategicTag ? (
            <span className="rounded-md bg-[var(--header-shell-bg)]/92 px-2.5 py-1 font-display text-[0.56rem] font-semibold uppercase leading-tight tracking-[0.05em] text-white shadow-md shadow-black/20 ring-1 ring-white/25 backdrop-blur-sm">
              {strategicTag}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/55 to-transparent px-4 pb-5 pt-16">
        <p className="font-body text-[0.8125rem] font-medium tracking-wide text-white/85">{locationLine(d)}</p>
        {d.property_type ? (
          <p className="mt-1 font-body text-[0.65rem] font-medium uppercase tracking-wide text-white/70">{d.property_type}</p>
        ) : null}
        <p className="mt-1.5 font-display text-lg font-bold leading-tight uppercase tracking-[0.04em] text-white drop-shadow-sm md:text-xl">
          {d.title}
        </p>
      </div>
    </>
  );

  if (isExample) {
    return (
      <div className={`${CARD_SHELL} cursor-default`} role="article" aria-label="Sample listing (not a live offering)">
        {cardInner}
      </div>
    );
  }

  return (
    <Link to={`/deals/${d.id}`} className={CARD_SHELL}>
      {cardInner}
    </Link>
  );
}

export function Deals() {
  const { session } = useAuth();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('All');
  /** Lets the most recent /api/deals request clear loading — avoids a stuck spinner when an older fetch is cancelled after session resolves. */
  const dealsFetchGen = useRef(0);

  useEffect(() => {
    const gen = ++dealsFetchGen.current;
    (async () => {
      try {
        const res = await apiFetch<{ deals: Deal[] }>('/api/deals', {
          token: session?.access_token,
        });
        if (dealsFetchGen.current !== gen) return;
        setDeals(res.deals);
        setErr(null);
      } catch (e) {
        if (dealsFetchGen.current !== gen) return;
        setErr((e as Error).message);
      } finally {
        if (dealsFetchGen.current === gen) setLoading(false);
      }
    })();
  }, [session?.access_token]);

  const displayDeals = deals.length > 0 ? deals : EXAMPLE_DEALS;
  const showingSamples = deals.length === 0;

  const regionsPresent = useMemo(() => {
    const set = new Set<string>();
    let other = false;
    for (const d of displayDeals) {
      const r = regionForDeal(d);
      if (r) set.add(r);
      else other = true;
    }
    const ordered = STATE_FILTERS.map((x) => x.label).filter((l) => set.has(l));
    return { pills: ordered, hasOther: other };
  }, [displayDeals]);

  const filteredDeals = useMemo(() => {
    if (filter === 'All') return displayDeals;
    if (filter === 'Other') return displayDeals.filter((d) => !regionForDeal(d));
    return displayDeals.filter((d) => regionForDeal(d) === filter);
  }, [displayDeals, filter]);

  const openCount = useMemo(
    () => displayDeals.filter((d) => d.status === DEAL_STATUSES.OPEN).length,
    [displayDeals],
  );

  const heroThumbnails = useMemo(
    () => displayDeals.slice(0, 9).map((d) => resolveCover(d)),
    [displayDeals],
  );

  const [heroSlide, setHeroSlide] = useState(0);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;
    const id = window.setInterval(() => {
      setHeroSlide((i) => (i + 1) % 9);
    }, 5200);
    return () => window.clearInterval(id);
  }, [prefersReducedMotion]);

  if (loading) {
    return (
      <div className="mx-auto max-w-[90rem] px-6 py-24 font-body text-sm text-[var(--home-muted-on-dark)]">
        Loading offerings…
      </div>
    );
  }
  if (err) {
    const missingBase = import.meta.env.PROD && !(envConfig.apiBaseUrl ?? '').trim();
    const sameHostMisconfig = import.meta.env.PROD && apiBasePointsAtCurrentHost(envConfig.apiBaseUrl ?? '');
    const showConfigHint = missingBase || sameHostMisconfig;
    return (
      <div className="mx-auto max-w-[42rem] px-6 py-24 font-body text-sm text-rose-200">
        <p className="font-semibold text-rose-100">Could not load offerings</p>
        <p className="mt-3 leading-relaxed text-[var(--home-muted-on-dark)]">{err}</p>
        {showConfigHint ? (
          <div className="mt-6 space-y-3 rounded-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/70 p-4 leading-relaxed text-[var(--home-text-on-dark)] ring-1 ring-white/[0.04]">
            {missingBase ? (
              <p>
                Production builds must set{' '}
                <code className="rounded bg-black/35 px-1 py-0.5 font-mono text-[11px] text-[var(--home-miami-blue)]">
                  VITE_API_BASE_URL
                </code>{' '}
                (or{' '}
                <code className="rounded bg-black/35 px-1 py-0.5 font-mono text-[11px] text-[var(--home-miami-blue)]">
                  NEXT_PUBLIC_API_BASE_URL
                </code>
                , or <code className="font-mono text-[11px]">API_BASE_URL</code>) to your{' '}
                <strong>Express API</strong> origin — never this SPA hostname. Example:{' '}
                <code className="rounded bg-black/35 px-1 py-0.5 font-mono text-[11px]">https://your-api.vercel.app</code>.
                Empty → the browser calls <code className="font-mono text-[11px]">/api</code> here and fails.
                <span className="mt-2 block text-[var(--home-muted-on-dark)]">
                  Vercel SPA project: Dashboard → Settings → Environment Variables → Production → add{' '}
                  <code className="font-mono text-[11px]">VITE_API_BASE_URL</code>, then trigger a new deployment so the bundle
                  rebuilds. Cloudflare Pages: same variable under your Pages project → Settings → Variables.
                </span>
              </p>
            ) : null}
            {sameHostMisconfig ? (
              <p>
                <code className="rounded bg-black/35 px-1 py-0.5 font-mono text-[11px] text-[var(--home-miami-blue)]">
                  VITE_API_BASE_URL
                </code>{' '}
                matches this site’s hostname, but this deployment is only the SPA. Point it at the Vercel project that runs{' '}
                <code className="font-mono text-[11px]">api/index.js</code> / Express (<code className="font-mono text-[11px]">
                  /api/deals
                </code>
                ), then redeploy this client.
              </p>
            ) : null}
          </div>
        ) : (
          <p className="mt-4 text-[var(--home-muted-on-dark)]">
            Check that the API is up, <code className="font-mono text-[11px]">CLIENT_ORIGIN</code> includes this site for CORS, and the
            URL above is correct.
          </p>
        )}
      </div>
    );
  }

  const filterIds = ['All', ...regionsPresent.pills, ...(regionsPresent.hasOther ? (['Other'] as const) : [])];

  return (
    <div className="pb-28">
      {/* Hero — sits on marketing turf; only a soft veil so type stays readable */}
      <section className="relative overflow-hidden border-b border-white/[0.08]">
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[var(--home-zone-void)]/72 via-[var(--home-zone-void)]/40 to-transparent"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35]"
          aria-hidden
          style={{
            backgroundImage: `radial-gradient(rgba(255,255,255,0.16) 1px, transparent 1.6px)`,
            backgroundSize: '22px 22px',
          }}
        />

        <div className="relative mx-auto max-w-[90rem] px-4 py-14 md:px-8 lg:px-10 lg:py-20">
          <div
            className="relative min-h-[min(52vw,22rem)] overflow-hidden rounded-2xl border border-white/12 shadow-[0_28px_80px_-40px_rgb(0_0_0/0.9)] ring-1 ring-black/35 sm:min-h-[20rem] lg:min-h-[22rem]"
            role="region"
            aria-roledescription="carousel"
            aria-label="Featured program photography"
          >
            <div className="absolute inset-0 bg-neutral-950" aria-hidden>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => {
                const url = heroThumbnails[i];
                const active = i === heroSlide;
                return (
                  <div
                    key={i}
                    className={`absolute inset-0 transition-opacity duration-[1100ms] ease-in-out ${
                      active ? 'z-[1] opacity-100' : 'z-0 opacity-0'
                    }`}
                  >
                    {url ? (
                      <img src={url} alt="" className="h-full w-full object-cover" loading={i === 0 ? 'eager' : 'lazy'} />
                    ) : (
                      <div
                        className="h-full w-full bg-gradient-to-br opacity-95"
                        style={{
                          backgroundImage: `linear-gradient(135deg, #2d2822 0%, #3d3530 50%, var(--home-zone-void) 100%)`,
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            <div
              className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-r from-[var(--home-zone-void)]/94 via-[var(--home-zone-void)]/72 to-[var(--home-zone-void)]/15 md:via-[var(--home-zone-void)]/55"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-t from-black/55 via-transparent to-black/25 sm:from-black/50"
              aria-hidden
            />

            <div className="relative z-[3] flex min-h-[min(52vw,22rem)] flex-col justify-center px-5 py-10 sm:min-h-[20rem] sm:px-8 lg:min-h-[22rem] lg:px-12 lg:py-14">
              <div className="max-w-xl space-y-5 text-[var(--home-text-on-dark)] drop-shadow-[0_2px_12px_rgb(0_0_0/0.55)]">
                <div className="space-y-2 font-display text-[clamp(1.25rem,2.6vw,1.85rem)] font-bold uppercase leading-[1.06] tracking-[0.04em] md:space-y-2.5">
                  <p>
                    {displayDeals.length} PROGRAM{displayDeals.length === 1 ? '' : 'S'} · {openCount} LIVE
                  </p>
                  <p className="text-white/[0.98]">
                    MULTIFAMILY <span className="mx-2 text-[var(--home-muted-on-dark)]">|</span> MIXED-USE CRE
                  </p>
                  <p className="text-[clamp(1rem,1.8vw,1.25rem)] text-white/[0.95]">
                    INSTITUTIONAL WORKFLOWS <span className="mx-2 opacity-50">|</span>{' '}
                    <span className="text-[var(--header-shell-bg)] drop-shadow-[0_1px_2px_rgb(0_0_0/0.45)]">
                      ACCREDITED ACCESS
                    </span>
                  </p>
                </div>
                <p className="max-w-xl font-body text-[0.9rem] leading-relaxed text-white/88 md:text-[0.95rem]">
                  Review live and historical offerings that pair conservative structures with disciplined reporting. Targets and
                  terms are illustrative — each series is governed by its private placement memorandum.
                </p>
              </div>
            </div>

            <div
              className="absolute bottom-4 left-1/2 z-[4] flex -translate-x-1/2 gap-1.5 rounded-full border border-white/10 bg-black/35 px-2.5 py-1.5 backdrop-blur-sm"
              role="tablist"
              aria-label="Slide indicators"
            >
              {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={heroSlide === i}
                  tabIndex={heroSlide === i ? 0 : -1}
                  onClick={() => setHeroSlide(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    heroSlide === i ? 'w-5 bg-[var(--header-shell-bg)]' : 'w-1.5 bg-white/35 hover:bg-white/55'
                  }`}
                >
                  <span className="sr-only">Slide {i + 1}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Title + filters + grid */}
      <div className="mx-auto max-w-[90rem] px-4 pt-14 md:px-8 lg:px-10">
        <header className="mx-auto max-w-3xl text-center">
          <h1 className="font-display text-3xl font-bold uppercase tracking-[0.06em] text-[var(--home-text-on-dark)] md:text-4xl">
            Current investments
          </h1>
          <p className="mt-4 font-body text-[0.98rem] leading-relaxed text-[var(--home-muted-on-dark)] md:text-base">
            Offerings aligned with underwriting you can diligence — allocations are limited and communicated per closes in the
            PPM subscription process.
          </p>
        </header>

        <div className="mt-10 rounded-2xl border border-white/15 bg-neutral-950/45 px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-md md:px-6">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <span className="font-display text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white/90">Filter</span>
            <div className="flex flex-wrap gap-2">
              {filterIds.map((id) => {
                const active = filter === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setFilter(id)}
                    className={`rounded-full px-4 py-2 font-display text-[0.65rem] font-semibold uppercase tracking-[0.08em] transition ${
                      active
                        ? 'bg-[var(--header-shell-bg)]/85 text-[var(--home-zone-void)] shadow-md shadow-black/20 ring-2 ring-white/25'
                        : 'bg-black/35 text-white/90 ring-1 ring-white/15 hover:bg-black/45'
                    }`}
                  >
                    {id}
                  </button>
                );
              })}
            </div>
          </div>
          {showingSamples ? (
            <p className="mt-4 font-body text-[0.8rem] leading-relaxed text-[var(--home-muted-on-dark)]">
              Sample listings below — publish live programs from Admin to replace these placeholders.
            </p>
          ) : null}
        </div>

        {filteredDeals.length === 0 ? (
          <p className="mt-14 text-center font-body text-sm text-[var(--home-muted-on-dark)]">
            Nothing in this geography — choose another filter.
          </p>
        ) : (
          <ul className="mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {filteredDeals.map((d) => {
              const tags = EXAMPLE_TAGS[d.id] ?? EXAMPLE_TAGS_BY_TITLE[d.title];
              return (
                <li key={d.id}>
                  <DealCard
                    d={d}
                    condition={tags?.condition ?? d.property_class ?? undefined}
                    strategicTag={tags?.strategicTag ?? d.strategy ?? undefined}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
